import { BaseDomain, ApiClient, tokenManager, requestManager, PaginationService } from '../shared';
import type { ApiErrorResponse, BaseApiError } from '../shared';
import type {
  CreatePatientNoteRequest,
  PatientNote,
  PatientNoteListRequest,
  PatientNoteApi,
  PaginatedPatientNotes,
  PaginatedPatientNotesApi,
  UpdatePatientNoteRequest,
} from '@/types/patientNotes';
import { PatientNotesApiError } from '@/types/patientNotes';

const ENDPOINT = '/dietitians';

/** Shape of an error thrown by the API client, as far as this domain reads it */
type ThrownApiError = Partial<Pick<BaseApiError, 'message' | 'status' | 'fieldErrors' | 'code'>> &
  Partial<Pick<ApiErrorResponse, 'detail'>>;

export class PatientNotesDomain extends BaseDomain<PatientNotesApiError> {
  constructor() {
    const apiClient = new ApiClient<PatientNotesApiError>(
      (message: string, status?: number, fieldErrors?: Record<string, string>, code?: string) =>
        new PatientNotesApiError(message, status, fieldErrors, code)
    );
    super(apiClient, tokenManager, requestManager);
  }

  protected getDomainName(): string {
    return 'PatientNotes';
  }

  protected createDomainError(
    operation: string,
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ): PatientNotesApiError {
    return new PatientNotesApiError(message, status, fieldErrors, code);
  }

  private buildEndpoint(dietitianId: string, patientId: string): string {
    return `${ENDPOINT}/${dietitianId}/patients/${patientId}/notes`;
  }

  async listNotes(request: PatientNoteListRequest): Promise<PaginatedPatientNotes> {
    return this.executeWithRetry(async () => {
      if (!request.dietitianId || !request.patientId) {
        throw this.createError('listNotes', 'dietitianId and patientId are required', 400);
      }

      const page = request.page ?? 1;
      const pageSize = request.pageSize ?? 10;
      const validation = PaginationService.validate({ page, perPage: pageSize });
      if (!validation.isValid) {
        throw this.createError(
          'listNotes',
          'Invalid pagination parameters',
          400,
          this.buildFieldErrorsFromValidation(validation.errors),
          'INVALID_PAGINATION'
        );
      }

      const query = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
      });

      try {
        const response = await this.apiClient.get<PaginatedPatientNotesApi>(
          `${this.buildEndpoint(request.dietitianId, request.patientId)}?${query.toString()}`
        );
        return this.transformPaginatedPatientNotes(response);
      } catch (error) {
        throw this.handleApiError(error);
      }
    });
  }

  async createNote(request: CreatePatientNoteRequest): Promise<PatientNote> {
    return this.executeWithRetry(async () => {
      if (!request.dietitianId || !request.patientId) {
        throw this.createError('createNote', 'dietitianId and patientId are required', 400);
      }

      const content = request.contentMarkdown?.trim();
      if (!content) {
        throw this.createError('createNote', 'contentMarkdown must not be empty', 400, undefined, 'EMPTY_CONTENT');
      }
      if (content.length > 10_000) {
        throw this.createError('createNote', 'contentMarkdown exceeds 10000 characters', 400, undefined, 'CONTENT_TOO_LONG');
      }

      const payload: Record<string, unknown> = {
        content_markdown: content,
      };

      try {
        const response = await this.apiClient.post<PatientNoteApi>(
          this.buildEndpoint(request.dietitianId, request.patientId),
          payload
        );
        return this.transformPatientNote(response);
      } catch (error) {
        throw this.handleApiError(error);
      }
    });
  }

  async updateNote(request: UpdatePatientNoteRequest): Promise<PatientNote> {
    return this.executeWithRetry(async () => {
      if (!request.dietitianId || !request.patientId || !request.noteId) {
        throw this.createError('updateNote', 'dietitianId, patientId, and noteId are required', 400);
      }

      const content = request.contentMarkdown?.trim();
      if (!content) {
        throw this.createError('updateNote', 'contentMarkdown must not be empty', 400, undefined, 'EMPTY_CONTENT');
      }
      if (content.length > 10_000) {
        throw this.createError('updateNote', 'contentMarkdown exceeds 10000 characters', 400, undefined, 'CONTENT_TOO_LONG');
      }

      const payload: Record<string, unknown> = {
        content_markdown: content,
      };

      try {
        const response = await this.apiClient.put<PatientNoteApi>(
          `${this.buildEndpoint(request.dietitianId, request.patientId)}/${request.noteId}`,
          payload
        );
        return this.transformPatientNote(response);
      } catch (error) {
        throw this.handleApiError(error);
      }
    });
  }

  private transformPaginatedPatientNotes(payload: PaginatedPatientNotesApi): PaginatedPatientNotes {
    return {
      items: (payload.items || []).map((note) => this.transformPatientNote(note)),
      page: payload.page,
      pageSize: payload.page_size,
      totalItems: payload.total_items,
    };
  }

  private transformPatientNote(note: PatientNoteApi): PatientNote {
    return {
      id: note.id,
      patientId: note.patient_id,
      authorId: note.author_id,
      contentMarkdown: note.content_markdown,
      createdAt: note.created_at,
      updatedAt: note.updated_at,
    };
  }

  private buildFieldErrorsFromValidation(errors: string[]): Record<string, string> {
    const fieldErrors: Record<string, string> = {};
    for (const error of errors) {
      if (error.includes('Page number')) {
        fieldErrors.page = error;
      } else if (error.includes('Page size')) {
        fieldErrors.perPage = error;
      } else {
        fieldErrors.general = error;
      }
    }
    return fieldErrors;
  }

  private handleApiError(error: unknown): PatientNotesApiError {
    if (error instanceof PatientNotesApiError) {
      return error;
    }

    const apiError = error as ThrownApiError | null | undefined;
    const detail = typeof apiError?.detail === 'string' ? apiError.detail : undefined;
    const message = apiError?.message || detail || 'Failed to fetch notes';
    const status = apiError?.status;
    const fieldErrors = apiError?.fieldErrors;
    const code = apiError?.code;

    return this.createError('patient_notes', message, status, fieldErrors, code);
  }
}

export const patientNotesDomain = new PatientNotesDomain();
